<script setup lang="ts">
import { ref } from 'vue'
import axios, { baseUrl } from '@/api'

const email = ref<string | null>(null)
const password = ref<string | null>(null)
const errorMessage = ref<string | null>(null)

const login = async () => {
  try {
    const response = await axios.post('/api/v1/user/login', {
      email: email.value,
      password: password.value,
    })

    if (response.data.success) {
      location.reload()
    } else {
      errorMessage.value = 'Your login credentials are invalid.'
    }
  } catch {
    errorMessage.value = 'Login failed. Please check your connection and try again.'
  }
}

// Floating-label form styling (Bootstrap 3-era metrics; root font-size is 10px)
const loginInput =
  'peer block w-full text-[18px] pt-[10px] pr-[10px] pb-[10px] pl-[5px] border border-[#ccc] ' +
  'focus:outline-none'
const loginLabel =
  'text-[#999] text-[18px] font-normal absolute pointer-events-none left-[5px] top-[10px] ' +
  'transition-all duration-200 peer-focus:-top-[20px] peer-focus:text-[14px] ' +
  'peer-focus:text-[#003c4d] peer-valid:-top-[20px] peer-valid:text-[14px] ' +
  'peer-valid:text-[#003c4d]'
const loginBar =
  "relative block w-full before:content-[''] before:absolute before:h-[2px] before:w-0 " +
  'before:bottom-[1px] before:bg-[#003c4d] before:transition-all before:duration-200 ' +
  "before:left-1/2 after:content-[''] after:absolute after:h-[2px] after:w-0 " +
  'after:bottom-[1px] after:bg-[#003c4d] after:transition-all after:duration-200 ' +
  'after:right-1/2 peer-focus:before:w-1/2 peer-focus:after:w-1/2'
</script>

<template>
  <div>
    <h1 class="text-center font-light pb-[40px]">Login</h1>

    <div v-if="errorMessage" class="block -mt-[30px] mb-[30px] text-center text-[#a94442]">
      <strong>{{ errorMessage }} </strong>
    </div>

    <form
      class="login max-w-[440px] mx-auto px-[20px]"
      role="form"
      method="POST"
      @submit.prevent="login"
    >
      <div class="relative mb-[35px]">
        <input v-model="email" type="email" name="email" required autofocus :class="loginInput" />
        <span :class="loginBar" />
        <label for="email" :class="loginLabel">E-Mail Address</label>
      </div>

      <div class="relative mb-[35px]">
        <input v-model="password" type="password" name="password" required :class="loginInput" />
        <span :class="loginBar" />
        <label for="password" :class="loginLabel">Password</label>
      </div>

      <div class="relative mb-[35px] text-right">
        <button
          type="submit"
          class="inline-block font-normal text-center whitespace-nowrap align-middle touch-manipulation cursor-pointer select-none border no-underline hover:no-underline focus:no-underline active:outline-0 px-[16px] py-[10px] text-[18px] leading-[1.3333333] rounded-[6px] text-white bg-[#32a8d9] border-[#32a8d9] hover:bg-[#286090] hover:border-[#204d74] focus:bg-[#286090] focus:border-[#204d74] active:bg-[#286090] active:shadow-[inset_0_3px_5px_rgba(0,0,0,0.125)]"
        >
          Login
        </button>
      </div>

      <a
        :href="`${baseUrl}/apps/workos/login/google`"
        class="relative top-[70px] flex items-center justify-center w-full px-[18px] py-[15px] mb-0 text-[14px] font-normal leading-[1.3333333] text-center whitespace-nowrap touch-manipulation cursor-pointer select-none border-none rounded-none text-[#636363] bg-white shadow-[0_2px_4px_0_rgba(0,0,0,0.25)] outline-none no-underline hover:no-underline hover:text-[#333] hover:bg-[#e6e6e6] focus:no-underline"
      >
        <img
          src="../assets/google-sign-in.png"
          class="absolute left-[20px] w-[20px]"
          alt="Google logo"
        />
        Log in with Google
      </a>
    </form>
  </div>
</template>
